import {Button, Form, Modal} from "react-bootstrap";
import DataTable from "datatables.net-react";
import {portForwardingDialogManager} from "./PortForwardingDialogManager.tsx";
import type {ChangeEvent, FormEvent} from "react";
import type {InstanceDataProps, Port} from "../../lib/ModelTypes.ts";
import {datatable_language_pack} from "../../components/data_table/DatatableLanguagePack.tsx";
import {useTranslation} from "react-i18next";
import type {Config} from "datatables.net";

type DataTableProps = {
    show: boolean;
    close: () => void;
    data: InstanceDataProps;
    setData: (data: (prev: InstanceDataProps) => InstanceDataProps) => void;
};

export const PortForwardingDialog = ({show, close, data, setData}: DataTableProps) => {
    const {t} = useTranslation();

    async function onSubmit(e: FormEvent<HTMLFormElement>) {
        await portForwardingDialogManager.onSubmit(e, data.selectedId, data.proto, data.port, setData);
    }

    function updatePort(e: ChangeEvent<HTMLInputElement>) {
        const port = e.target.value;
        setData((prev: InstanceDataProps) => ({...prev, port}));
    }

    function updateProto(e: ChangeEvent<HTMLSelectElement>) {
        const proto = e.target.value;
        setData((prev: InstanceDataProps) => ({...prev, proto}));
    }

    const port_columns = [
        {
            data: 'id',
            orderable: false,
            render() {
                return "<span>pf.blueshift.xslab.co.kr</span>";
            },
        },
        {
            data: 'proto',
            render(data: string) {
                return `<span class="protocol" data-protocol='${data}'>${data}</span>`;
            },
        },
        {
            data: 'intern_port', className: "align-center",
            render(data: number) {
                return `<span class="intern-port" data-intern-port='${data}'>${data}</span>`;
            },
        },
        {
            data: 'extern_port', className: "align-center",
            render(data: number) {
                return `<span class="extern-port" data-extern-port='${data}'>${data}</span>`;
            },
        },
        {
            data: 'id',
            render() {
                return `<button class="button_red px-2 py-1 rounded-3 delete">${t("portForwarding.delete")}</button>`;
            },
            orderable: false,
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        }
    ];

    const options: Config = {
        destroy: true,
        paging: false,
        searching: false,
        order: [[1, "asc"]],
        ordering: true,
        lengthChange: false,
        language: datatable_language_pack,
        info: false,
        createdRow(row: Node, port: Port | object, dataIndex: number) {
            const htmlRow = row as HTMLTableRowElement;
            const deleteButton = htmlRow.querySelector('.delete');
            if ('proto' in port) {
                if (deleteButton) {
                    deleteButton.addEventListener(
                        'click',
                        () => portForwardingDialogManager.onClickDelete(data.selectedId, port.proto, port.intern_port, port.id, dataIndex, setData)
                    );
                }
            }
        }
    }

    return (
        <Modal show={show} onHide={close} className="modal-lg">
            <Modal.Header>
                <Modal.Title><h6 className="text-primary pt-2">{t("title.portForwardingSettings")}</h6></Modal.Title>
                <Button variant="white" className="btn_close" onClick={close}>X
                </Button>
            </Modal.Header>
            <Modal.Body className="pt-1 px-1">
                <Form className="modal-body row pb-0" onSubmit={onSubmit}>
                    <div className="row">
                        <p className="col-md-5 pt-2">
                            <label htmlFor="port_instance_name">{t("instance.instanceName")}</label>
                        </p>
                        <div className="col-md-7">
                            <input
                                id="port_instance_name"
                                type="text"
                                className="form-control text-secondary"
                                value={data.selectedName} disabled/>
                        </div>
                    </div>

                    <div className="row mt-2">
                        <p className="col-md-5 pt-2"><label htmlFor="port">{t("portForwarding.protocol")}</label></p>
                        <div className="col-md-7">
                            <select id="protocol" name="protocol" value={data.proto} onChange={updateProto}
                                    className="form-select">
                                <option value="TCP">TCP</option>
                                <option value="UDP">UDP</option>
                            </select>
                        </div>
                    </div>

                    <div className="row mt-2">
                        <p className="col-md-5 pt-2"><label htmlFor="port">{t("portForwarding.internalPort")}</label></p>
                        <div className="col-md-7">
                            <input id="port" type="text" onChange={updatePort}
                                   className="form-control text-secondary"/>
                        </div>
                    </div>

                    <div className="row mt-2">
                        <div className="col-md-12">
                            <button className="button_blue py-1 rounded-3 w-100" type="submit">
                                <p style={{fontSize: "14px", fontWeight: "300"}}>{t("common.add")}</p>
                            </button>
                        </div>
                    </div>
                </Form>
                <div className="mx-3">
                    <DataTable data={data.portTableData} columns={port_columns} className="display" options={options}
                               key={data.portTableData.length}>
                        <thead>
                        <tr>
                            <th>{t("portForwarding.address")}</th>
                            <th>{t("portForwarding.protocol")}</th>
                            <th>{t("portForwarding.internalPort")}</th>
                            <th>{t("portForwarding.externalPort")}</th>
                            <th>{t("common.action")}</th>
                        </tr>
                        </thead>
                        {
                            data.portTableData.length === 0 ? <tbody>
                                <tr>
                                    <td colSpan={5}>{t("instance.loadingList")}</td>
                                </tr>
                                </tbody> :
                                <tbody></tbody>
                        }
                    </DataTable>
                </div>
            </Modal.Body>
        </Modal>
    )
}